import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { localNow, type DayNutrition, type HomeSummary, type Meal, type User, type Workout } from '@jungles/shared';
import { api, loadToken } from '../lib/api';
import { HomeNutrition } from './HomeNutrition';
import { foodQuickCopy, nextHomeMeal, workoutQuickCopy, type HomeWorkouts, type QuickCopy } from '../lib/home-quick';
import '../styles/home-quick.css';

// Presentation only: fit the actual font/available width, not a viewport guess.
// Keep ordinary copy untouched; shorten static words only below the 10px floor.
function QuickText({ copy }: { copy: QuickCopy }) {
  const title = useRef<HTMLElement>(null);
  const secondary = useRef<HTMLSpanElement>(null);
  const lines = copy.title.split('\n');
  const draft = lines[1]?.startsWith('«');
  const compact = (text = '') => text.replace('упражнений', 'упр.').replace('Цель недели', 'Неделя')
    .replace('Сегодня ', '').replace(' к цели', '').replace(' из ', '/').replace(/(\d)[\s\u00a0]+(?=\d{3}(?:\D|$))/g, '$1');
  useLayoutEffect(() => {
    const heading = title.current!, detail = secondary.current!;
    let active = true;
    const fit = () => {
      if (!active) return;
      heading.style.fontSize = ''; detail.style.fontSize = ''; delete detail.dataset.compact;
      const headingBase = parseFloat(getComputedStyle(heading).fontSize);
      const detailBase = parseFloat(getComputedStyle(detail).fontSize);
      const width = (el: Element) => { const r = document.createRange(); r.selectNodeContents(el); return r.getBoundingClientRect().width; };
      const headingLines = Array.from(heading.children).slice(0, draft ? 1 : 2);
      const headingWidth = Math.max(...headingLines.map(width));
      let headingSize = Math.min(headingBase, headingBase * heading.clientWidth / Math.max(1, headingWidth));
      heading.style.fontSize = `${headingSize}px`;
      while (headingSize > 10 && Math.max(...headingLines.map(width)) > heading.clientWidth) {
        headingSize = Math.max(10, headingSize - 0.1); heading.style.fontSize = `${headingSize}px`;
      }
      const fitDetail = (visible: Element) => {
        const needed = width(visible);
        let size = Math.min(detailBase, Math.max(10, detailBase * (detail.clientWidth - 0.5) / Math.max(1, needed)));
        detail.style.fontSize = `${size}px`;
        // Glyph hinting is not perfectly proportional at fractional font sizes.
        while (size > 10 && width(visible) > detail.clientWidth) {
          size = Math.max(10, size - 0.1); detail.style.fontSize = `${size}px`;
        }
      };
      fitDetail(detail.children[0]!);
      if (width(detail.children[0]!) > detail.clientWidth) {
        detail.dataset.compact = 'true'; detail.style.fontSize = `${detailBase}px`;
        fitDetail(detail.children[1]!);
      }
    };
    fit();
    const observer = new ResizeObserver(fit); observer.observe(heading); observer.observe(detail);
    document.fonts.ready.then(fit);
    return () => { active = false; observer.disconnect(); };
  }, [copy.title, copy.detail, copy.accent, copy.suffix, draft]);
  return <>
    <b ref={title} title={copy.title}>{lines.map((line, i) => <span key={i}>{line}</span>)}</b>
    <span className="st" ref={secondary}>
      <span className="quick-full">{copy.detail}{copy.accent ? <em>{copy.accent}</em> : null}{copy.suffix}</span>
      <span className="quick-compact" aria-hidden="true">{compact(copy.detail)}{copy.accent ? <em>{compact(copy.accent)}</em> : null}{compact(copy.suffix)}</span>
    </span>
  </>;
}

type Action = { key: string; copy: QuickCopy; run: () => void; blocked?: boolean };
function QuickCard({ action, kind, onHold }: { action: Action; kind: 'workout' | 'food'; onHold: (held: boolean) => void }) {
  const [held, setHeld] = useState<Action | null>(null);
  const cancelled = useRef(false);
  const pointerStart = useRef({ x: 0, y: 0 });
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const shown = held ?? action;
  const release = () => { clearTimeout(timer.current); timer.current = setTimeout(() => { setHeld(null); onHold(false); }, 350); };
  useEffect(() => () => clearTimeout(timer.current), []);
  return <button type="button" className={`qk home-qk ${kind}${shown.copy.hot ? ' hot' : ''}`}
    aria-label={`${shown.copy.title.replace('\n', ' ')}. ${shown.copy.detail}${shown.copy.accent ?? ''}${shown.copy.suffix ?? ''}`}
    onPointerDown={e => {
      cancelled.current = false;
      pointerStart.current = { x: e.clientX, y: e.clientY };
      onHold(true);
      clearTimeout(timer.current);
      setHeld(action);
      e.currentTarget.setPointerCapture(e.pointerId);
    }}
    onPointerUp={release}
    onPointerMove={e => { if (Math.hypot(e.clientX - pointerStart.current.x, e.clientY - pointerStart.current.y) > 10) cancelled.current = true; }}
    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') cancelled.current = false; }}
    onPointerCancel={() => { cancelled.current = true; release(); }}
    onLostPointerCapture={release}
    onClick={e => {
      // Keep the same physical button through revalidation and its compatibility click.
      // A changed/deleted draft or a day/meal transition consumes, never redirects, this gesture.
      if (!cancelled.current && !action.blocked && shown.key === action.key) action.run();
      else e.preventDefault();
      release();
    }}>
    <QuickText copy={shown.copy} />
    <img src={`/assets/ui/tab-${kind === 'workout' ? 'workouts' : 'food'}.webp`} alt="" />
  </button>;
}

export function HomeQuickButtons({ user, home, summary, draft, summaryLoading, summaryError, onWorkout, onFood, onNewDay, onHold }: {
  user: User; home: HomeSummary; summary: HomeWorkouts | null; draft: Workout | null;
  summaryLoading: boolean; summaryError: boolean;
  onWorkout: (draft: Workout | null) => void;
  onFood: (meal: Meal, date: string) => void;
  onNewDay: () => void;
  onHold: (held: boolean) => void;
}) {
  const [now, setNow] = useState(() => localNow(user.timezone));
  const [nutrition, setNutrition] = useState<DayNutrition | null>(null);
  const [nutritionLoading, setNutritionLoading] = useState(true);
  const [nutritionFailed, setNutritionFailed] = useState(false);
  const [nonce, setNonce] = useState(0);
  const lastDate = useRef(now.date);
  const newDay = useRef(onNewDay); newDay.current = onNewDay;
  useEffect(() => {
    const tick = () => {
      const next = localNow(user.timezone);
      if (lastDate.current !== next.date) { lastDate.current = next.date; newDay.current(); }
      setNow(prev => prev.time === next.time && prev.date === next.date ? prev : next);
    };
    const refresh = () => { if (document.visibilityState === 'visible') { tick(); setNonce(n => n + 1); } };
    tick();
    const id = setInterval(tick, 1000);
    window.addEventListener('focus', refresh);document.addEventListener('visibilitychange', refresh);
    return () => { clearInterval(id);window.removeEventListener('focus', refresh);document.removeEventListener('visibilitychange', refresh); };
  }, [user.timezone]);
  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    const token = loadToken();
    setNutritionLoading(true); setNutritionFailed(false);
    api.get<{ day: DayNutrition }>(`/api/nutrition/day?date=${now.date}`, controller.signal)
      .then(value => { if (active && token === loadToken()) { setNutrition(value.day?.date === now.date ? value.day : null); setNutritionLoading(false); } })
      .catch(() => { if (active && token === loadToken()) { setNutrition(null); setNutritionLoading(false); setNutritionFailed(true); } });
    return () => { active = false; controller.abort(); };
  }, [now.date, nonce, user.id]);
  const day = nutrition?.date === now.date ? nutrition : null;
  const meal = nextHomeMeal(now.time, day);
  const validSummary = !summaryError ? summary : null;
  const validDraft = !summaryError ? draft : null;
  const workout: Action = {
    key: validDraft ? `draft:${validDraft.id}` : 'new',
    copy: workoutQuickCopy(validDraft, validSummary, now.date, home.today === now.date ? home.week : null),
    blocked: summaryLoading,
    run: () => onWorkout(validDraft),
  };
  const food: Action = {
    key: `${now.date}:${meal}`,
    copy: foodQuickCopy(meal, day, user.dailyCalorieTarget),
    run: () => onFood(meal, now.date),
  };
  return <><div className="quick home-quick"><QuickCard action={workout} kind="workout" onHold={onHold} /><QuickCard action={food} kind="food" onHold={onHold} /></div>
    <HomeNutrition day={day} date={now.date} target={user.dailyCalorieTarget} loading={nutritionLoading}
      failed={nutritionFailed} onRetry={() => setNonce(n => n + 1)} /></>;
}
